<template>
    <div>
        <h3 v-if="paymentsWithDeadlineToDisplay.length > 0">
            {{ $l10n.__('Liste des paiements liés à des échéances') }}
        </h3>
        <h3 v-else>{{ $l10n.__('Ajouter un nouveau règlement') }}</h3>

        <p class="aright">
            <CompassButton
                v-if="hasReconciledPayment"
                :label="wordingBtnCollapse"
                type="tertiary"
                @click="togglePaymentsDisplay"
            />
        </p>

        <el-table
            v-show="paymentsWithDeadlineToDisplay.length > 0"
            :data="paymentsWithDeadlineToDisplay"
            :rowKey="rowKey"
            :class="{ 'b-1 br': true, 'br-bottom-0': paymentsToAdd.length > 0 }"
            style="width: 100%"
        >
            <el-table-column :label="$t.__('common.labels.date')" width="100">
                <template #default="scope">
                    {{ $filters.saasDateFormatted(scope.row.date) }}
                </template>
            </el-table-column>
            <el-table-column prop="ref" :label="$t.__('common.labels.reference')">
            </el-table-column>
            <el-table-column :label="$t.__('common.labels.payment_amount')" width="180">
                <template #default="scope">
                    {{ $filters.numberFormatCurrency(scope.row.amount) }}
                </template>
            </el-table-column>
            <el-table-column :label="$t.__('common.labels.payment_status')" width="150">
                <template #default="scope">
                    <div v-if="scope.row.payStepLabel !== undefined">
                        <div :class="'chip chip-' + scope.row.payStepChip"></div>
                        {{ scope.row.payStepLabel }}
                    </div>
                    <div v-else>
                        {{ $l10n.__('Nouveau paiement') }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                :label="$t.__('bankreconciliation.DeadlineListing.related_deadline')"
                width="150"
            >
                <template #default="scope">
                    <el-popover placement="bottom" width="200" trigger="hover">
                        <div v-for="deadline in scope.row.deadlines" :key="deadline.date">
                            <strong>{{ $filters.saasDateFormatted(deadline.date) }}</strong
                            ><br />
                            <span style="color: #7f868f">{{ $l10n.__('Reste à solder : ') }}</span>
                            {{ $filters.numberFormatCurrency(deadline.remainingAmount) }}
                        </div>
                        <template #reference>
                            <strong>
                                {{
                                    $l10n.n__(
                                        '1 échéance',
                                        '%d échéances',
                                        scope.row.deadlines.length,
                                        scope.row.deadlines.length,
                                    )
                                }}
                            </strong>
                        </template>
                    </el-popover>
                </template>
            </el-table-column>
            <el-table-column :label="$t.__('common.actions.reconcile')" width="140">
                <template #default="scope">
                    <el-checkbox
                        v-model="scope.row.isChecked"
                        :disabled="scope.row.isReconciled"
                        @change="
                            updateStore(
                                scope.row.storeId,
                                scope.row.payid,
                                deadlineIds(scope.row.deadlines),
                                $event,
                            )
                        "
                    >
                        {{ $l10n.__('Rapproché') }}
                    </el-checkbox>
                </template>
            </el-table-column>
        </el-table>

        <!-- Form to add payment -->
        <el-table
            v-show="paymentsToAdd.length > 0"
            :data="paymentsToAdd"
            :rowKey="rowKeyNewPayment"
            :class="{
                'el-table--disable-row-hover br': true,
                'b-1': !paymentsToDisplay.length,
                'bl-1 br-1 bb-1 br-top-0': paymentsToDisplay.length > 0,
            }"
            style="width: 100%"
        >
            <el-table-column :label="$t.__('common.labels.date')" width="100">
                <template #default="scope">
                    {{ $filters.saasDateFormatted(scope.row.date) }}
                </template>
            </el-table-column>
            <el-table-column :label="$t.__('common.labels.reference')">
                <template #default="scope">
                    <el-input
                        v-model="scope.row.ref"
                        :placeholder="$t.__('common.labels.a_reference')"
                        :disabled="scope.row.isLocked"
                    ></el-input>
                </template>
            </el-table-column>
            <el-table-column :label="$t.__('common.labels.payment_amount')" width="180">
                <template #default="scope">
                    <input-number
                        v-model="scope.row.amount"
                        :precision="numberFormat.precision"
                        :separatorDecimal="numberFormat.decimal"
                        :separatorThousand="numberFormat.thousand"
                        :disabled="scope.row.isLocked"
                        :controls="false"
                        :placeholder="$t.__('common.labels.amount')"
                        size="small"
                        :min="0.01"
                        :max="dueAmountDeadline"
                    >
                    </input-number>
                </template>
            </el-table-column>
            <el-table-column
                :label="$t.__('bankreconciliation.DeadlineListing.related_deadline')"
                width="150"
            >
                <template #default="scope">
                    <el-select
                        v-model="scope.row.deadlineIds"
                        multiple
                        :placeholder="$t.__('bankreconciliation.DeadlineListing.link_deadline')"
                        @change="updateAmount"
                    >
                        <el-option
                            v-for="deadline in onlyRemaining"
                            :key="deadline.deadlineid"
                            :label="$filters.saasDateFormatted(deadline.date)"
                            :value="deadline.deadlineid"
                        >
                            <strong>{{ $filters.saasDateFormatted(deadline.date) }}</strong>
                            <span style="color: #7f868f">
                                {{ $l10n.__('Reste à solder : ') }}
                            </span>
                            {{ $filters.numberFormatCurrency(deadline.remainingAmount) }}
                        </el-option>
                    </el-select>
                </template>
            </el-table-column>
            <el-table-column :label="$t.__('common.actions.reconcile')" width="140">
                <template #default="scope">
                    <i
                        class="icon-trash link"
                        style="float: right"
                        @click="removeSelectedLine(scope.row.id)"
                    ></i>
                    <rb-new-payment-deadline-checkbox
                        :default="false"
                        :document="document"
                        :reference="scope.row.ref"
                        :amount="scope.row.amount"
                        :deadlines="getDeadlines(scope.row.deadlineIds)"
                        @new-payment-confirmed="removeSelectedLine()"
                    />
                </template>
            </el-table-column>
        </el-table>

        <p class="aright">
            <CompassButton
                v-if="canAddPayment"
                :label="$t.__('common.actions.add_and_reconcile__new_payment')"
                type="tertiary"
                @click="addNewPayment"
            />
        </p>
    </div>
</template>

<script>
import Mixin from './../../mixins/interlineReconciliation.js';
import NewPaymentDeadlineCheckbox from './NewPaymentDeadlineCheckbox.vue';
import InputNumber from 'vcomponents/utilities/InputNumber.vue';

export default {
    components: {
        'input-number': InputNumber,
        'rb-new-payment-deadline-checkbox': NewPaymentDeadlineCheckbox,
    },
    mixins: [Mixin],
    props: ['document', 'payments', 'deadlines'],
    computed: {
        paymentsWithDeadlineToDisplay: function () {
            return this.paymentsToDisplay.map((payment) => {
                if ('deadlines' in payment) {
                    return payment;
                }
                payment.deadlines = this.deadlines.filter((deadline) => {
                    return deadline.payid == payment.payid;
                });
                return payment;
            });
        },
        onlyRemaining: function () {
            return this.deadlines.filter((deadline) => {
                return deadline.remainingAmount > 0;
            });
        },
        // calculated according to the selected deadlines
        dueAmountDeadline: function () {
            const newPayment = this.paymentsToAdd[0];

            if (!newPayment) {
                return this.dueAmount;
            }

            if (newPayment.deadlineIds.length === 0) {
                return this.dueAmount;
            }

            return this.sumDeadLines();
        },
    },
    watch: {
        paymentsToDisplay: function (newArray, oldArray) {
            if (newArray.length == 0 && this.paymentsToAdd.length == 0) {
                this.addNewPayment();
            }
        },
    },
    methods: {
        updateStore: function (storeid, paymentid, deadlineids, check) {
            if (check) {
                const selectedPayment = this.payments.find((payment) => {
                    return payment.payid == paymentid;
                });

                const reconciliation = {
                    paymentid: paymentid,
                    amount: selectedPayment.amount,
                    deadlineids: deadlineids,
                    document: this.document,
                    id: '_' + Math.random().toString(36).substr(2, 9),
                };
                this.addReconciled(reconciliation);
            } else {
                const currentPayment = this.reconciled.find((payment) => {
                    return payment.id == storeid;
                });
                this.delReconciled(storeid);

                if (paymentid === undefined) {
                    currentPayment.deadlineIds = currentPayment.deadlines.map((deadline) => {
                        return deadline.deadlineid;
                    });
                    this.paymentsToAdd.unshift(currentPayment);
                }
            }
        },
        addNewPayment: function () {
            const payment = {
                id: '_' + Math.random().toString(36).substr(2, 9),
                ref: '',
                date: this.transactionDate,
                amount: this.defaultNewPayment,
                deadlineIds: [],
                deadlineDates: [],
                isLocked: false,
            };

            if (this.onlyRemaining.length > 0) {
                payment.amount = this.onlyRemaining[0].remainingAmount;
                payment.deadlineIds.push(this.onlyRemaining[0].deadlineid);
                payment.deadlineDates.push(this.onlyRemaining[0].date);
            }

            this.paymentsToAdd.push(payment);
        },
        deadlineIds: function (deadlines) {
            return deadlines.map((deadline) => {
                return deadline.deadlineid;
            });
        },
        getDeadlines: function (ids) {
            return this.deadlines.filter((deadline) => {
                return ids.includes(deadline.deadlineid);
            });
        },
        sumDeadLines: function () {
            const newPayment = this.paymentsToAdd[0];

            if (newPayment.deadlineIds.length === 0) {
                return 0;
            }

            const selectedDeadlines = this.deadlines.filter((deadline) => {
                return newPayment.deadlineIds.includes(deadline.deadlineid);
            });

            const remainingAmounts = selectedDeadlines.map((deadline) => {
                return parseFloat(deadline.remainingAmount);
            });

            return remainingAmounts.reduce((sum, amount) => sum + amount);
        },
        updateAmount: function (ids) {
            const newPayment = this.paymentsToAdd[0];
            newPayment.amount = this.sumDeadLines();

            this.paymentsToAdd = [];
            this.paymentsToAdd.push(newPayment);
        },
    },
};
</script>
